import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router';

import { SearchOutlined } from '@mui/icons-material';

import { SearchType } from 'app/constants';
import useIsMobile from 'hooks/use-is-mobile';
import useShortcutSearchInput from 'hooks/use-shortcut-search-input';
import useStores from 'hooks/use-stores';
import AutoCompleteInput from 'pages/layout/components/SearchBar/components/AutoCompleteInput';
import useSuggestions from 'pages/layout/components/SearchBar/hooks/use-suggestions';

import useStyles from './styles';

interface Props {
    className?: string;
}

const SearchBar: React.FC<Props> = ({ className }) => {
    const { classes, cx } = useStyles();
    const history = useHistory();
    const { searchStore } = useStores();

    const autoCompleteInputRef = useShortcutSearchInput();
    const isMobile = useIsMobile();

    const [value, setValue] = useState({
        type: SearchType.simple,
        query: '',
        software: '',
        version: '',
        state: { searchTerm: '' }
    });

    const { suggestions, isLoading } = useSuggestions(value);

    const handleChange = useCallback(
        (query: string) =>
            setValue((value) => ({
                ...value,
                query,
                state: { searchTerm: query.trim(), sortDirection: 'desc', sortField: 'published' }
            })),
        []
    );

    const handleSubmit = useCallback(() => {
        const { type, query, software, version, state } = value;
        if (query) {
            searchStore.setParameters({ type, query, software, version, state });
            searchStore.performFetch();
            history.push(`/search?query=${query}`);
        }
    }, [history, searchStore, value]);

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                handleSubmit();
            }
        },
        [handleSubmit]
    );

    return (
        <AutoCompleteInput
            id="__search_bar_autocomplete_input__"
            ref={autoCompleteInputRef}
            loading={isLoading}
            className={cx(classes.root, className)}
            classes={{
                list: classes.list,
                input: classes.input,
                option: classes.option,
                matched: classes.matched,
                highlight: classes.highlight
            }}
            value={value.query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            options={suggestions}
            startAdornment={
                value.query ? null : (
                    <div className={classes.placeholder}>
                        <SearchOutlined />
                        <span className={classes.ellipsis}>
                            {isMobile ? 'Search' : 'Searching through 3M+ vulnerabilities and exploits'}
                        </span>
                    </div>
                )
            }
        />
    );
};
export default observer(SearchBar);
